import { useEffect, useRef } from 'preact/hooks';
import { GuideCompanion } from '../components/GuideCompanion';

// Pixel art letters — each letter is an array of [col, row] in global grid coords
// Origin (0,0) = anchor cell at top-left of logo; x+ = right, y+ = down
const LETTERS: Record<string, [number, number][]> = {
	p: [[0,0],[2,0],[3,0],[4,1],[4,2],[4,3],[3,4],[2,4],[1,1],[1,2],[1,3],[1,4],[1,5],[1,6],[0,7],[1,7],[2,7]],
	x1: [[5,0],[6,1],[7,2],[8,3],[9,4],[9,0],[8,1],[7,2],[6,3],[5,4]],
	t1: [[10,-2],[10,-1],[10,0],[10,1],[10,2],[10,3],[11,4],[9,0],[10,0],[11,0],[12,0]],
	x2: [[12,0],[13,1],[14,2],[15,3],[16,4],[16,0],[15,1],[14,2],[13,3],[12,4]],
	t2: [[17,-2],[17,-1],[17,0],[17,1],[17,2],[17,3],[18,4],[16,0],[17,0],[18,0],[19,0]],
};

const DEBUG_COORDS = false;

// Animation timing
const CELL_DELAY = 60;
const STROKE_PAUSE = 150;
const LETTER_OVERLAP = 0.3; // next letter starts when previous is this fraction from done

// Cells after which to insert a stroke pause (lifting the "pen")
const STROKE_BREAKS: Set<string> = new Set(['0,0', '2,4', '1,6', '9,4', '11,4', '16,4', '18,4']);

// Compute raw duration of a single letter (without overlap)
function letterDuration(name: string): number {
	let d = 0;
	for (const [col, row] of LETTERS[name]) {
		d += CELL_DELAY;
		if (STROKE_BREAKS.has(`${col},${row}`)) d += STROKE_PAUSE;
	}
	return d;
}

// Build a flat sequence with absolute timing for each cell
function buildSequence(): { col: number; row: number; t: number; flash: boolean }[] {
	const seq: { col: number; row: number; t: number; flash: boolean }[] = [];
	const seen = new Set<string>();
	const letterNames = Object.keys(LETTERS);
	let letterStart = 0;
	for (let i = 0; i < letterNames.length; i++) {
		if (i > 0) {
			const prevDur = letterDuration(letterNames[i - 1]);
			letterStart = letterStart + prevDur * (1 - LETTER_OVERLAP);
		}
		let t = letterStart;
		for (const [col, row] of LETTERS[letterNames[i]]) {
			const key = `${col},${row}`;
			seq.push({ col, row, t, flash: seen.has(key) });
			seen.add(key);
			t += CELL_DELAY;
			if (STROKE_BREAKS.has(key)) t += STROKE_PAUSE;
		}
	}
	return seq;
}

function drawGrid(canvas: HTMLCanvasElement, elapsed: number = Infinity) {
	const hero = canvas.parentElement;
	const headline = hero?.querySelector('.hero-headline') as HTMLElement | null;
	if (!hero || !headline) return;

	const dpr = window.devicePixelRatio || 1;
	const ctx = canvas.getContext('2d');
	if (!ctx) return;

	// Cell size: caps span ~1.4 cells (matching Figma reference)
	// Roboto Slab cap-height ≈ 0.71 × fontSize → cell = fontSize * 0.5
	const fontSize = parseFloat(getComputedStyle(headline).fontSize);
	const cell = fontSize * 0.5;

	// Push cell into CSS (line-height = 3 cells, padding scales)
	hero.style.setProperty('--cell', `${cell}px`);

	// Size canvas (forces reflow with updated --cell)
	canvas.style.height = '';
	const w = canvas.offsetWidth;
	const h = canvas.offsetHeight;
	canvas.width = w * dpr;
	canvas.height = h * dpr;
	ctx.scale(dpr, dpr);

	// Measure font metrics for baseline computation
	ctx.font = `300 ${fontSize}px ${getComputedStyle(headline).fontFamily}`;
	const metrics = ctx.measureText('Hg');
	const fontAscent = metrics.fontBoundingBoxAscent;
	const fontDescent = metrics.fontBoundingBoxDescent;

	// Temporarily disable animation for accurate position reading
	const savedAnimation = headline.style.animation;
	headline.style.animation = 'none';
	headline.offsetHeight; // force reflow at final position

	// Compute baseline from font metrics (no DOM probe needed)
	const canvasRect = canvas.getBoundingClientRect();
	const hlRect = headline.getBoundingClientRect();
	const lineHeightPx = 2 * cell;
	const halfLeading = (lineHeightPx - fontAscent - fontDescent) / 2;
	const baselineY = (hlRect.top - canvasRect.top) + halfLeading + fontAscent;

	// Restore animation
	headline.style.animation = savedAnimation;

	// Anchor grid so a grid line passes through the baseline
	const gridOffsetY = baselineY % cell;

	// Anchor X to headline left edge
	const anchorX = hlRect.left - canvasRect.left;
	const gridOffsetX = anchorX % cell;

	// Last full grid row — don't draw past this
	const gridMaxY = Math.floor((h - gridOffsetY) / cell) * cell + gridOffsetY;

	// Detect dark mode for canvas colors
	const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
		&& document.documentElement.getAttribute('data-theme') !== 'light'
		|| document.documentElement.getAttribute('data-theme') === 'dark';
	const gridRgb = isDark ? '230, 230, 230' : '34, 34, 34';

	// Draw grid lines
	ctx.strokeStyle = `rgba(${gridRgb}, 0.05)`;
	ctx.lineWidth = 1;

	for (let x = gridOffsetX; x <= w; x += cell) {
		ctx.beginPath();
		ctx.moveTo(Math.round(x) + 0.5, 0);
		ctx.lineTo(Math.round(x) + 0.5, gridMaxY);
		ctx.stroke();
	}

	for (let y = gridOffsetY; y <= gridMaxY; y += cell) {
		ctx.beginPath();
		ctx.moveTo(0, Math.round(y) + 0.5);
		ctx.lineTo(w, Math.round(y) + 0.5);
		ctx.stroke();
	}

	// Pixel art fills
	// Origin = anchor point; cells placed at origin + (col, row) * cell
	const originX = anchorX - cell;
	const originY = baselineY - 2 * cell; // cap top
	const inset = cell * 0.1;
	const fillSize = cell - inset * 2;

	const sequence = buildSequence();
	// First pass: draw fills (skip flash duplicates)
	for (const { col, row, t, flash } of sequence) {
		if (flash || elapsed < t) continue;

		const x = originX + col * cell;
		const y = originY + row * cell;
		if (x + fillSize < 0 || x > w || y + fillSize < 0 || y > h) continue;

		const age = elapsed - t;
		const alpha = Math.min(age / 150, 1) * 0.06;

		ctx.fillStyle = `rgba(${gridRgb}, ${alpha})`;
		ctx.fillRect(x + inset, y + inset, fillSize, fillSize);

		if (DEBUG_COORDS) {
			const label = `${col},${row}`;
			const fontSize2 = Math.max(8, cell * 0.35);
			ctx.font = `${fontSize2}px monospace`;
			ctx.fillStyle = `rgba(${gridRgb}, ${Math.min(age / 150, 1) * 0.5})`;
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.fillText(label, x + cell / 2, y + cell / 2);
		}
	}

	// Second pass: draw white flashes over overlap cells
	for (const { col, row, t, flash } of sequence) {
		if (!flash || elapsed < t) continue;

		const x = originX + col * cell;
		const y = originY + row * cell;
		if (x + fillSize < 0 || x > w || y + fillSize < 0 || y > h) continue;

		const age = elapsed - t;
		const flashAlpha = Math.max(0, 1 - age / 300) * 0.15;
		if (flashAlpha > 0) {
			ctx.fillStyle = isDark ? `rgba(0, 0, 0, ${flashAlpha})` : `rgba(255, 255, 255, ${flashAlpha})`;
			ctx.fillRect(x + inset, y + inset, fillSize, fillSize);
		}
	}

	// Snap CTA button to grid
	const cta = hero.querySelector('.btn') as HTMLElement | null;
	const bodyEl = hero.querySelector('.hero-body') as HTMLElement | null;
	if (cta && bodyEl) {
		// Reset so we measure natural position
		cta.style.width = '';
		cta.style.height = '';
		cta.style.marginLeft = '';
		bodyEl.style.marginBottom = '';

		const ctaRect = cta.getBoundingClientRect();
		const btnLeft = ctaRect.left - canvasRect.left;
		const btnTop = ctaRect.top - canvasRect.top;
		const btnRight = btnLeft + ctaRect.width;

		// Find enclosing grid lines
		const snapTop = Math.round((btnTop - gridOffsetY) / cell) * cell + gridOffsetY;
		const snapBottom = snapTop + cell;
		const snapLeft = Math.floor((btnLeft - gridOffsetX) / cell) * cell + gridOffsetX;
		const snapRight = Math.ceil((btnRight - gridOffsetX) / cell) * cell + gridOffsetX;

		// Adjust body margin to push button top onto grid line
		const currentMargin = parseFloat(getComputedStyle(bodyEl).marginBottom);
		bodyEl.style.marginBottom = `${currentMargin + (snapTop - btnTop)}px`;

		// Set exact dimensions to span grid lines
		cta.style.marginLeft = `${snapLeft - btnLeft}px`;
		cta.style.width = `${snapRight - snapLeft}px`;
		cta.style.height = `${snapBottom - snapTop}px`;
	}
}

export function Landing() {
	const sectionsRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);

	// Grid canvas — animate pixel art sequence
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		let raf = 0;
		let startTime = 0;
		let onResize: (() => void) | null = null;

		const totalDuration = buildSequence().reduce((max, s) => Math.max(max, s.t), 0) + 500;

		const animate = (now: number) => {
			if (!startTime) startTime = now;
			const elapsed = now - startTime;
			drawGrid(canvas, elapsed);
			if (elapsed < totalDuration) {
				raf = requestAnimationFrame(animate);
			}
		};

		const init = () => {
			// Compute when "px" and "txt" finish from the sequence
			const seq = buildSequence();
			const letterNames = Object.keys(LETTERS);
			let pxEnd = 0;
			let txtEnd = 0;
			const pxLetters = new Set(['p', 'x1']);
			let cellIdx = 0;
			for (const name of letterNames) {
				for (let i = 0; i < LETTERS[name].length; i++) {
					const s = seq[cellIdx++];
					if (pxLetters.has(name)) pxEnd = Math.max(pxEnd, s.t);
					else txtEnd = Math.max(txtEnd, s.t);
				}
			}

			// Line 1 finishes 400ms before px ends; line 2 finishes 400ms before txt ends
			const animDuration = 600;
			const line1 = canvas.parentElement?.querySelector('.hero-line-1') as HTMLElement;
			const line2 = canvas.parentElement?.querySelector('.hero-line-2') as HTMLElement;
			const line1Delay = Math.max(0, pxEnd - animDuration - 400);
			const line2Delay = Math.max(0, txtEnd - animDuration - 400);
			if (line1) line1.style.animationDelay = `${line1Delay}ms`;
			if (line2) line2.style.animationDelay = `${line2Delay}ms`;

			// Subtitle sits between the headline and the body — fades in once line 2 has settled
			const subtitle = canvas.parentElement?.querySelector('.hero-subtitle') as HTMLElement;
			if (subtitle) subtitle.style.animationDelay = `${line2Delay + 200}ms`;

			// Body fades in smoothly after the subtitle
			const body = canvas.parentElement?.querySelector('.hero-body') as HTMLElement;
			if (body) body.style.animationDelay = `${line2Delay + 500}ms`;

			onResize = () => drawGrid(canvas, Infinity);
			window.addEventListener('resize', onResize);
			raf = requestAnimationFrame(animate);
		};

		document.fonts.ready.then(init);
		return () => {
			cancelAnimationFrame(raf);
			if (onResize) window.removeEventListener('resize', onResize);
		};
	}, []);

	// Scroll reveal
	useEffect(() => {
		const root = sectionsRef.current;
		if (!root) return;
		const sections = root.querySelectorAll('.landing-section');
		if (!sections.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						entry.target.classList.add('is-visible');
						observer.unobserve(entry.target);
					}
				}
			},
			{ threshold: 0.15 }
		);

		sections.forEach((s) => observer.observe(s));
		return () => observer.disconnect();
	}, []);

	return (
		<div class="page" ref={sectionsRef}>
			<header class="landing-header">
				<div class="container">
					<nav class="landing-nav">
						<a href="/" class="logo">
							<span class="logo-text">pxtxt</span>
						</a>
						<a href="/sign-in" class="landing-sign-in">Sign in</a>
					</nav>
				</div>
			</header>

			<main class="landing-main">
				<div class="container">
					<section class="hero" data-guide-section="hero">
						<canvas ref={canvasRef} class="hero-grid" aria-hidden="true" />
						<h1 class="hero-headline">
							<span class="hero-line hero-line-1">Your best work</span><br />
							<span class="hero-line hero-line-2">is invisible</span>
						</h1>
						<p class="hero-subtitle">Know your story before they ask.</p>
						<p class="hero-body">
							It's buried in team efforts, locked behind NDAs, and
							flattened into bullet points. pxtxt surfaces the depth
							through conversation — what you built, how you
							thought about it, why it mattered — and turns it into
							a professional narrative you own and a representation
							agent that speaks on your behalf when you're not in
							the room.
						</p>
						<a href="/sign-up" class="btn">Begin your interview</a>
					</section>

					<section class="landing-section process" data-guide-section="process">
						<div class="process-item">
							<div class="process-step">
								<svg class="process-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M4 2.5H12C12.8284 2.5 13.5 3.17157 13.5 4V8C13.5 8.82843 12.8284 9.5 12 9.5H4.75L4.59961 9.7002L2.5 12.499V4C2.5 3.17157 3.17157 2.5 4 2.5Z" stroke="currentColor"/>
								</svg>
								<span class="process-label">Interview</span>
							</div>
							<p class="process-desc">
								You talk about your work. The agent
								keeps asking for specifics — decisions
								you made, problems you solved, context
								that resumes leave out.
							</p>
						</div>
						<div class="process-item">
							<div class="process-step">
								<svg class="process-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M10.5 3H11C12.1046 3 13 3.89543 13 5V12C13 13.1046 12.1046 14 11 14H5C3.89543 14 3 13.1046 3 12V5C3 3.89543 3.89543 3 5 3H5.5" stroke="currentColor"/>
									<rect x="5.5" y="2.5" width="5" height="2" rx="1" stroke="currentColor"/>
								</svg>
								<span class="process-label">Narrative</span>
							</div>
							<p class="process-desc">
								One conversation produces a professional
								narrative — what you actually did, how you
								thought about it, why it mattered. Plus a
								resume and data export. You own all of it.
							</p>
						</div>
						<div class="process-item">
							<div class="process-step">
								<svg class="process-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<circle cx="8" cy="5.5" r="2.5" stroke="currentColor"/>
									<path d="M3.5 13.5C3.5 11.0147 5.51472 9 8 9C10.4853 9 12.5 11.0147 12.5 13.5" stroke="currentColor" stroke-linecap="round"/>
								</svg>
								<span class="process-label">Profile</span>
							</div>
							<p class="process-desc">
								Your profile goes live with a representation
								agent. Visitors ask questions. It answers from
								what you actually said. You see what they cared
								about.
							</p>
						</div>
					</section>

					<section class="landing-section conversation" data-guide-section="conversation">
						<h2 class="conversation-headline">The conversation does the work</h2>
						<p class="conversation-body">
							No templates. No writing prompts. You talk about what
							you've done, and the agent extracts structure from it.
							Come back when there's new work to talk about, or when
							time has given you a different view of the old.
						</p>
					</section>

					<section class="landing-section tiers" data-guide-section="tiers">
						<div class="tiers-grid">
							<div class="tier-col">
								<span class="tier-name">Free</span>
								<ul class="tier-features">
									<li>Interview session</li>
									<li>Professional narrative</li>
									<li>Published profile with a representation agent</li>
									<li>Engagement insights</li>
									<li>Resume + data export</li>
								</ul>
							</div>
							<div class="tier-col">
								<span class="tier-name"><span class="tier-price">$12/mo</span> <span class="tier-annual">or $120/year</span></span>
								<ul class="tier-features">
									<li>Unlimited sessions</li>
									<li>Full-depth narrative</li>
									<li>Ongoing engagement reports</li>
									<li>Custom profile URL</li>
									<li>Company intelligence</li>
								</ul>
							</div>
						</div>
					</section>

					<section class="landing-section cta-footer" data-guide-section="cta-footer">
						<a href="/sign-up" class="btn">Begin your interview</a>
					</section>
				</div>
			</main>

			<footer class="landing-footer">
				<div class="container">
					<div class="footer-row">
						<p class="footer-brand"><span class="footer-logo">pxtxt</span> — built by CaliKo LLC&ensp;&middot;&ensp;Your data is yours. Export anytime. The schema is open.</p>
						<nav class="footer-links">
							<a href="/terms">Terms</a>
							<a href="/privacy">Privacy</a>
						</nav>
					</div>
				</div>
			</footer>

			<GuideCompanion page="landing" />

			<style>{`
				/* ── Header ────────────────────────────────── */

				.landing-header {
					padding: 1rem 0;
					border-bottom: 1px solid var(--color-border);
					background: var(--color-bg-elevated);
					position: sticky;
					top: 0;
					z-index: 100;
				}

				.landing-nav {
					display: flex;
					align-items: center;
					justify-content: space-between;
				}

				.logo {
					text-decoration: none;
				}

				.logo-text {
					font-family: var(--font-display);
					font-size: 1.5rem;
					font-weight: 700;
					color: var(--color-title);
					letter-spacing: -0.02em;
				}

				.landing-sign-in {
					font-family: var(--font-mono);
					font-size: 12px;
					text-transform: uppercase;
					letter-spacing: 0.08em;
					color: var(--color-text-muted);
					text-decoration: none;
					transition: color 200ms var(--ease-out-quart);
				}

				.landing-sign-in:hover {
					color: var(--color-text);
				}

				/* ── Landing main ──────────────────────────── */

				.landing-main {
					flex: 1;
				}

				.landing-main > .container {
					max-width: 720px;
				}

				/* ── Hero ──────────────────────────────────── */

				@keyframes landing-fade-up {
					from { opacity: 0; transform: translateY(16px); }
					to { opacity: 1; transform: translateY(0); }
				}

				.hero {
					--title-size: clamp(40px, 6vw, 80px);
					--cell: calc(var(--title-size) * 0.5);
					position: relative;
					padding-top: calc(var(--cell) * 3);
					margin-bottom: calc(var(--cell) * 3);
				}

				.hero-line {
					display: inline;
					opacity: 0;
					animation: landing-fade-up 600ms var(--ease-out-expo) both;
				}

				.hero-subtitle {
					position: relative;
					margin: 0 0 var(--cell);
					font-family: var(--font-display);
					font-weight: 300;
					font-size: clamp(16px, 2vw, 24px);
					line-height: var(--cell);
					letter-spacing: -0.01em;
					color: var(--color-text-muted);
					max-width: 540px;
					opacity: 0;
					animation: landing-fade-up 800ms var(--ease-out-expo) both;
				}

				.hero-body {
					opacity: 0;
					animation: landing-fade-up 1000ms var(--ease-out-expo) both;
				}

				.hero > .btn {
				}

				/* ── Hero Grid Background ──────────────────── */

				.hero-grid {
					position: absolute;
					top: 0;
					bottom: 0;
					left: 50%;
					transform: translateX(-50%);
					width: 100vw;
					pointer-events: none;
				}

				.hero-headline {
					position: relative;
					margin: 0 0 var(--cell);
					font-family: var(--font-display);
					font-weight: 300;
					font-size: var(--title-size);
					line-height: calc(var(--cell) * 2);
					letter-spacing: -0.04em;
					color: var(--color-text);
				}

				.hero-body {
					position: relative;
					margin: 0 0 calc(var(--cell) * 2);
					font-family: var(--font-body);
					font-size: 15px;
					line-height: var(--cell);
					color: var(--color-text);
					max-width: 540px;
				}

				/* ── Process ───────────────────────────────── */

				.process {
					border-top: 1px solid var(--color-border);
					border-bottom: 1px solid var(--color-border);
					padding: 48px 0;
					display: flex;
					flex-direction: column;
					gap: 48px;
				}

				.process-item {
					display: grid;
					grid-template-columns: 180px 1fr;
					gap: 0 24px;
				}

				.process-step {
					display: flex;
					align-items: center;
					gap: 12px;
					padding-top: 2px;
				}

				.process-icon {
					width: 20px;
					height: 20px;
					flex-shrink: 0;
					color: var(--color-text);
				}

				.process-label {
					font-family: var(--font-mono);
					font-size: 12px;
					text-transform: uppercase;
					letter-spacing: 0.08em;
					color: var(--color-text-muted);
				}

				.process-desc {
					margin: 0;
					font-family: var(--font-body);
					font-size: 16px;
					line-height: 24px;
					color: var(--color-text);
				}

				/* ── Conversation ──────────────────────────── */

				.conversation {
					padding: 64px 0;
				}

				.conversation-headline {
					margin: 0 0 1.5rem;
					font-family: var(--font-display);
					font-weight: 300;
					font-size: 32px;
					line-height: 40px;
					color: var(--color-text);
				}

				.conversation-body {
					margin: 0;
					font-family: var(--font-body);
					font-size: 16px;
					line-height: 24px;
					color: var(--color-text-muted);
					max-width: 540px;
				}

				/* ── Tiers ─────────────────────────────────── */

				.tiers {
					border-top: 1px solid var(--color-border);
					padding: 48px 0;
				}

				.tiers-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: 48px;
				}

				.tier-name {
					display: block;
					font-family: var(--font-display);
					font-size: 24px;
					font-weight: 300;
					color: var(--color-text);
					margin-bottom: 20px;
					padding-bottom: 16px;
					border-bottom: 1px solid var(--color-border);
				}

				.tier-price {
					font-family: var(--font-display);
					font-size: 24px;
					font-weight: 700;
					color: var(--color-text);
				}

				.tier-annual {
					font-family: var(--font-body);
					font-size: 14px;
					font-weight: 400;
					color: var(--color-text-muted);
				}

				.tier-features {
					list-style: none;
					margin: 0;
					padding: 0;
					display: flex;
					flex-direction: column;
					gap: 8px;
				}

				.tier-features li {
					font-family: var(--font-body);
					font-size: 14px;
					line-height: 24px;
					color: var(--color-text);
				}

				/* ── CTA footer ────────────────────────────── */

				.cta-footer {
					padding: 32px 0;
				}

				/* ── Footer ────────────────────────────────── */

				.landing-footer {
					padding: 1rem 0;
					border-top: 1px solid var(--color-border);
					background: var(--color-bg-elevated);
				}

				.footer-row {
					display: flex;
					align-items: center;
					justify-content: space-between;
				}

				.footer-brand {
					margin: 0;
					font-family: var(--font-mono);
					font-size: 12px;
					color: var(--color-text-subtle);
				}

				.footer-logo {
					font-family: var(--font-display);
					font-weight: 700;
					color: var(--color-title);
				}

				.footer-links {
					display: flex;
					gap: 1.5rem;
					flex-shrink: 0;
				}

				.footer-links a {
					font-family: var(--font-mono);
					font-size: 12px;
					color: var(--color-text-muted);
					text-decoration: none;
					transition: color 200ms var(--ease-out-quart);
				}

				.footer-links a:hover {
					color: var(--color-text);
					text-decoration: underline;
				}

				/* ── Scroll reveal ─────────────────────────── */

				.landing-section {
					opacity: 0;
					transform: translateY(12px);
					transition: opacity 500ms var(--ease-out-expo),
					            transform 500ms var(--ease-out-expo);
				}

				.landing-section.is-visible {
					opacity: 1;
					transform: translateY(0);
				}

				/* ── Mobile ────────────────────────────────── */

				@media (max-width: 767px) {
					.hero-grid {
						top: 1rem;
					}

					.process {
						gap: 32px;
						padding: 32px 0;
					}

					.process-item {
						grid-template-columns: 1fr;
						gap: 8px;
					}

					.conversation {
						padding: 40px 0;
					}

					.tiers {
						padding: 32px 0;
					}

					.tiers-grid {
						grid-template-columns: 1fr;
						gap: 32px;
					}

					.cta-footer {
						padding: 24px 0;
					}

					.footer-row {
						flex-direction: column;
						align-items: flex-start;
						gap: 8px;
					}
				}
			`}</style>
		</div>
	);
}
